import { AsyncData, DjangoFormData, FormSetData } from '#src/vue/forms/FormData.mts'
import groupBy from 'lodash/groupBy'

export type FormUpdateData = {
    values: Record<string, string | string[] | number | undefined>
    initial: Record<string, string | string[] | number | undefined>
    errors: [string, string[]][]
}

export type AjaxResponseActions = {
    redirect: {
        href: string
        redirectData: any
    }
    showDjangoMessages: {
        messages: { msg: string, tags: string }[]
    }
    formDataUpdate: {
        form: FormUpdateData
        inlines: {
            prefix: string
            forms: Record<string, FormUpdateData>
        }[]
        success_message?: string
    }
    formData: {
        form: DjangoFormData
        inlines: FormSetData[]
        layout?: string
    }
    closeModal: null
}

export type RawAjaxResponseAction<Key extends keyof AjaxResponseActions> = { action: Key, data: AjaxResponseActions[Key] }
export type RawAjaxResponseActions = RawAjaxResponseAction<keyof AjaxResponseActions>[]

export type MessageData = AjaxResponseActions['showDjangoMessages']['messages']

type FilterFunction = (item: RawAjaxResponseAction<keyof AjaxResponseActions>) => boolean

export type ParsedAjaxResponse = {
        formMessages: Record<string, AjaxResponseActions["showDjangoMessages"]["messages"]>,
        messages: AjaxResponseActions["showDjangoMessages"]["messages"],
        formData: AjaxResponseActions["formData"],
        redirects: AjaxResponseActions["redirect"][],
}
export async function parseResponse(response: Response) {
    const body = await response.text()
    const json = JSON.parse(body)

    const raw_result = {
        formMessages: extractFormMessages(json).messages,
        messages: extractMessages(json).messages,
        formData: extractFormData(json).formData,
        redirects: extractRedirects(json).redirects,
    }

    const result = {
        ...raw_result,
        formData: (raw_result.formData?.[0]),
    }

    return result as ParsedAjaxResponse
}

export function getActions<Key extends keyof AjaxResponseActions>(key: Key, actions: RawAjaxResponseActions, filter?: FilterFunction) {
    const _actions = (actions ?? [])
        .slice()
        .map((item, index) => ({ item, index }))
        .filter(({item}) => item.action === key && (filter === undefined ? true : Boolean(filter?.(item))))
    const result = _actions.map(({item: {data}}) => data) as AjaxResponseActions[Key][]

    _actions.reverse().forEach(({index}) => {
        console.debug("splicing message", index, actions[index], [..._actions], [...actions])
        actions.splice(index, 1)
    })

    return result
}

// @ts-ignore
const formMessageFilter: FilterFunction = (item) => item.data?.messages?.some(message =>
    message.tags.startsWith('field-error')
    || message.tags.startsWith('field-valid')
    || message.tags.startsWith('__all__-'))

const FormErrorPrefix = 'field-error-'
const FormFeedbackPrefix = 'field-valid-'
function parseFormMessages(messages: { messages: MessageData}[]) {
    const _messages = messages.map(({messages}) => messages).flat()
    const result = groupBy(_messages, ({tags}) => {
        const tagArray = tags.split(' ')

        for (const prefix of [FormErrorPrefix, FormFeedbackPrefix]) {
            const fieldTag = tagArray
                .find(tag => tag.startsWith(prefix))
                ?.substring(prefix.length)

            if (fieldTag) {
                return fieldTag
            }
        }
        return 'undefined'
    }) as Record<string, MessageData>

    return result
}

/**
 * Removes and returns all response messages from the given action list.
 * @param actions The raw ajax response actions to extract messages from. The list will be mutated.
 *
 * @returns { messages: Generic messages, formMessages: form field validation responses}
 */
export function extractMessages(actions: RawAjaxResponseActions) {
    const _messages = getActions('showDjangoMessages', actions, (item: any) => !formMessageFilter(item))
    const { 'undefined': messages = [], ...formMessages } = parseFormMessages(_messages)

    return { messages }
}

/**
 * Removes and returns all form messages from the given action list.
 * @param actions The raw ajax response actions to extract messages from. The list will be mutated.
 *
 * @returns { messages: Generic messages, formMessages: form field validation responses}
 */
export function extractFormMessages(actions: RawAjaxResponseActions) {
    const _messages = getActions('showDjangoMessages', actions, formMessageFilter)
    const messages = parseFormMessages(_messages)

    return { messages }
}
/**
 * Removes and returns all redirect messages from the given action list.
 * @param actions The raw ajax response action list. The list will be mutated
 *
 * @returns { redirects: RedirectAction[], redirect: The first redirect message or null }
 */
export function extractRedirects(actions: RawAjaxResponseActions) {
    const redirects = getActions('redirect', actions)
    const redirect = (redirects?.[0] ?? null) as typeof redirects[0] | null

    return { redirects, redirect }
}
/**
 * Removes and returns all formData messages from the given action list.
 * @param actions The raw ajax response action list. The list will be mutated
 *
 * @returns { formData: Record<string, any> }
 */
export function extractFormData(actions: RawAjaxResponseActions) {
    const formData = getActions('formData', actions)
    return { formData }
}

export function extractAndRegisterFormMessages(props: AsyncData, actions: RawAjaxResponseActions) {
    const { form, inlines } = props
    const { messages, } = extractFormMessages(actions)

    form!.non_field_errors = []
    form!.non_field_feedback = []

    console.debug("Form response messages", messages)

    Object.entries(messages).forEach(([key, value]) => {
        const _msgs = value.map(({msg}) => msg)
        const is_error = value.some(item => item.tags.split(' ').some(tag => tag.startsWith(FormErrorPrefix)))
        if (key === '__all__') {
            if (is_error) {
                form!.non_field_errors = _msgs
            } else {
                form!.non_field_feedback = _msgs
            }
        } else {
            const [fieldName, ..._reversed_prefix] = key.split('-', 3).reverse()
            const prefix = _reversed_prefix?.length > 0
                ?_reversed_prefix?.reverse()?.join('-') ?? null
                : null

            const _form = [
                form,
                ...inlines?.map(({forms}) => forms).flat(1) ?? [],
            ].find((form) => form!.prefix === prefix)

            const _field = _form?.fields[fieldName]

            if (!_field) {
                console.debug("Could not find form field for validation message", key, _form, _field)
                if (is_error) {
                    form!.non_field_errors.push(..._msgs)
                } else {
                    form!.non_field_feedback.push(..._msgs)
                }
                return
            }

            if (is_error) {
                _field.errors = _msgs
            } else {
                _field.feedback = _msgs
            }
        }
    })
}

export function extractCloseModalActions(actions: RawAjaxResponseActions) {
    const closeModalActions = getActions('closeModal', actions)
    return { closeModal: (closeModalActions.length > 0), }
}

function _doUpdateFormData(form: DjangoFormData, update: FormUpdateData) {
    const { values, initial, errors } = update

    console.debug("Applying form update", update, form)

    for (const [key, value] of Object.entries(values)) {
        if (key in form.fields) {
            const field = form.fields[key]
            const currentValueIsArray = Array.isArray(field.value)
            if (currentValueIsArray) {
                // #TODO I really need to update the field type to make sure it helps rather than hinders
                // @ts-ignore
                field.value = Array.isArray(value) ? value : [value]
            } else {
                // @ts-ignore
                field.value = value
            }
        }
    }

    for (const [key, _errors] of (errors ?? [])) {
        if (key === '__all__') {
            form.non_field_errors = _errors
        }
        if (key in form.fields) {
            const field = form.fields[key]
            field.errors = _errors
        }
    }

}

export type FormData = AjaxResponseActions["formData"]
export function updateFormData(current: AsyncData, actions: RawAjaxResponseActions) {
    const formData = getActions('formDataUpdate', actions)?.[0]

    if (!formData) {
        return
    }
    if (!current) {
        return
    }

    const { form, inlines } = current

    const { form: formUpdateData, inlines: inlineUpdateData, success_message = undefined } = formData

    if (success_message) {
        current.form!.non_field_feedback = [success_message]
    }

    _doUpdateFormData(form!, formUpdateData)
    inlineUpdateData.forEach(({prefix: formsetPrefix, forms: formUpdateDict}) => {
        const formset = inlines?.find(formset => formset.prefix === formsetPrefix)
        if (!formset) {
            console.error("Could not find existing formset in update data", formsetPrefix, current, formData)
            return
        }

        Object.entries(formUpdateDict).forEach(([formPrefix, formUpdateData]) => {
            const form = formset?.forms.find(form => form.prefix === formPrefix)
            if (!form) {
                console.error("Could not find existing form from update data", formPrefix, formset.forms, formUpdateDict)
                return
            }

            _doUpdateFormData(form, formUpdateData)
        })
    })

    console.debug("formData", form!.non_field_feedback, form)

}
